import {
	EditorState,
	EditorStateConfig,
	Plugin,
	PluginKey,
	PluginView,
	TextSelection,
	Transaction
} from "prosemirror-state";
import {Decoration, DecorationSet, DecorationSource, EditorView} from "prosemirror-view";
import {MentionSuggestion, MentionSuggestions} from "@t/user/Mention.ts";
import {Icon, StyledIcon} from "anneca-symbols";
import type {I18nMsg} from "@t/util/Common.ts";

export const KEY = new PluginKey("mention");

export interface MentionOptions {
	suggest: (query:string) => Promise<MentionSuggestions>;
}

const DEFAULT_OPTIONS:MentionOptions = {
	suggest: async (query:string) => {
		console.log("Suggestion endpoint not configured: "+query);
		return { query, items:[] };
	}
};

type MentionTransaction =
	  { type: 'activate', from:number, to:number, query?:string }	// suggestion mode is activated with the first query
	| { type: 'update', from:number, to:number, query:string, commit?:boolean }
	| { type: 'deactivate', init:boolean }	// suggestion mode is deactivated without any other action
	| { type: 'data', mentions:MentionSuggestion[], selectedIdx:number, version: number }
	| { type: 'commit' }					// Insert the latest suggestion if there is any
	| { type: 'move', direction?:number, to?:number }	// Move selection in the suggestion list
	;

const START_QUERY = "@";

export interface MentionState {
	/** Suggestion mode is active */
	active: boolean;

	/**
	 * Bounds containing the label excluding @  (example: "The job will be done by @|john| asap")
	 * Moving the cursor outside of these boundaries will automatically cancel suggesting.
	 * from is determined when user types @ char
	 * to is extended as he types further
	 * @blesa
	 **/
	from: number;
	to: number;

	/** Actual query string. The text between from-to */
	query:string;
	queryVersion: number;

	/** Current mentions loaded from the server */
	mentions: MentionSuggestion[];
	/** Version of the mention data (incremented on each successful query */
	dataVersion: number;

	/** 0-based index of the selected suggestion */
	selectedIdx?:number;

	/** Signal that the commit should be performed immediately after query data arives with exactly one full match */
	commit?: "no" | "exact" | "any";
}

const INIT_STATE:MentionState = {
	active: false,
	from: -1,
	to: -1,

	query: START_QUERY,
	mentions: [],
	queryVersion: -1,
	dataVersion: -1,

	selectedIdx: -1,
	commit: "no"
}

const BOUNDS_CHAR = /[\s(,.;)?!\/\\:<>=\-$%\[\]{}|+&*]/u;

function isBoundaryChar(ch:string) {
	if (ch.length === 0) return true;	// is at start of the paragraph
	return BOUNDS_CHAR.test(ch);
}

function cleanState(state:MentionState):string {
	const s:MentionState = { ...state };
	delete s.mentions;
	return JSON.stringify(s);
}

function cleanTrans(trans:MentionTransaction):string {
	const t:MentionTransaction = { ...trans };
	if (t.type === 'data') delete t.mentions;
	return JSON.stringify(t);
}

function dumpState(msg:string, state:MentionState) {
	console.log(msg+": "+cleanState(state));
}

export function mentionPlugin(msg:I18nMsg, customOpts: Partial<MentionOptions> = {}) {
	const options = { ...DEFAULT_OPTIONS, ...customOpts };

	return new Plugin<MentionState>({
		key: KEY,
		props: {
			handleTextInput(view: EditorView, from: number, to: number, text: string):boolean|undefined {
				const state = view.state;
				const ms = KEY.getState(state);
				if (ms.active) {
					if (isBoundaryChar(text)) {
						// The user typed a boundary char
						// It can be typed at the end of the query or somewhere inside
						const { $from } = state.selection;
						//console.log("Typed boundary char [" + ms.from + ", " + ms.to + "] cursor " + $from.pos);
						const typedAtTheEnd = $from.pos === ms.to;
						const q = state.doc.textBetween(ms.from, ms.to);	// final query
						const tr = state.tr;
						if (q.length > 0 && typedAtTheEnd) {
							// We can try to auto-insert the query typed so far
							tr.insertText(text, $from.pos, $from.pos);
							tr.setMeta(KEY, <MentionTransaction>{
								type: 'update',
								from: ms.from,
								to: ms.to,
								query: q,
								commit: true
							});
							//console.log("=> dispatch autocommit with `"+text+"` append");
							view.dispatch(tr);
							return true;
						} else {
							tr.setMeta(KEY, <MentionTransaction>{ type: 'deactivate', init:true });
							/*tr.setMeta("addToHistory", false);*/
							//console.log("=> dispatch deactivate (empty query or invalid char)");
							view.dispatch(tr);
						}
					} else {
						// Extend the query
						const endPos = Math.max(from+1, ms.to+1);
						const q = state.doc.textBetween(ms.from, from) + text + state.doc.textBetween(from, ms.to);
						const tr = state.tr.setMeta(KEY, <MentionTransaction>{
							type: 'update',
							from: ms.from,
							to: endPos,
							query: q,
							commit: false
						});
						/*tr.setMeta("addToHistory", false);*/
						//console.log("=> dispatch update");
						view.dispatch(tr);
					}
				} else {
					if (text === '@') {
						// from is positioned just before @ char
						const prevChar = state.doc.textBetween(from - 1, from);
						//console.log("...input @ with prev char '"+prevChar+"'");
						if (!isBoundaryChar(prevChar)) return false;
						// Initiating suggestions
						const pos = from + 1;
						const tr = state.tr.setMeta(KEY, <MentionTransaction>{
							type: 'activate',
							from: pos,
							to: pos,
							query: ''	// starting with an empty query
						});
						/*tr.setMeta("addToHistory", false);*/
						tr.setMeta("separateHistory", true);
						//console.log("=> dispatch activate");
						view.dispatch(tr);
					}
				}
				return false;
			},

			decorations(state:EditorState):DecorationSource | null | undefined {
				const ms = KEY.getState(state)
				if (!ms.active) return null
				// highlight @ to the end of the query
				const deco = Decoration.inline(Math.max(ms.from - 1, 0), ms.to, {
					class: 'mention-query-highlight'
				})
				return DecorationSet.create(state.doc, [deco])
			},

			handleDOMEvents: {
				blur(view:EditorView, event) {
					const mention = KEY.getState(view.state)
					if (mention?.active) {
						const tr = view.state.tr.setMeta(KEY, { type: 'deactivate', init:true });
						//console.log("=> dispatch deactivate on blur");
						view.dispatch(tr);
					}
					return false;
				},

				keydown(view:EditorView, event:KeyboardEvent) {
					const mention = KEY.getState(view.state)
					if (!mention.active) return false

					switch (event.key) {
						case 'ArrowDown':
							event.preventDefault();
							//console.log("=> dispatch move DOWN");
							view.dispatch(view.state.tr.setMeta(KEY, <MentionTransaction>{ type: 'move', direction: 1 })/*.setMeta("addToHistory", false)*/);
							return true;

						case 'ArrowUp':
							event.preventDefault();
							//console.log("=> dispatch move UP");
							view.dispatch(view.state.tr.setMeta(KEY, <MentionTransaction>{ type: 'move', direction: -1 })/*.setMeta("addToHistory", false)*/);
							return true;

						case 'Enter':
						case 'Tab':
							event.preventDefault();
							//console.log("=> dispatch commit on Enter/Tab");
							view.dispatch(view.state.tr.setMeta(KEY, <MentionTransaction>{ type: 'commit' })/*.setMeta("addToHistory", false)*/);
							return true;

						case 'Escape':
							event.preventDefault();
							//console.log("=> dispatch deactivate on Escape");
							view.dispatch(view.state.tr.setMeta(KEY, <MentionTransaction>{ type: 'deactivate', init:true })/*.setMeta("addToHistory", false)*/);
							return true;
					}

					return false;
				}
			}
		},
		state: {
			init(config:EditorStateConfig, instance:EditorState):MentionState {
				return { ...INIT_STATE };
			},
			apply(tr:Transaction, prev: MentionState, oldState:EditorState, newState:EditorState):MentionState {
				let meta = tr.getMeta(KEY) as MentionTransaction | undefined;
				if (!meta && prev.active) {
					const cursorPos = newState.selection.head;
					if (cursorPos < prev.from || cursorPos > prev.to) {
						//console.log("...deactivating by selection: "+cursorPos+" outside of ["+prev.from+", "+prev.to+"]");
						meta = { type: "deactivate", init:false };
					} else if (tr.docChanged && newState.doc.nodeSize < oldState.doc.nodeSize) {
						const sizeDiff = oldState.doc.nodeSize - newState.doc.nodeSize;
						// probably a char was deleted inside the query
						let newQueryEnd = prev.to - sizeDiff;
						//console.log("...shrinking query from "+prev.to+" to "+ newQueryEnd);
						meta = {
							type: 'update',
							from: prev.from,
							to: newQueryEnd,
							query: newState.doc.textBetween(prev.from, newQueryEnd),
							commit: false
						};
					}
				}
				//----
				let ms:MentionState = prev;
				if (meta) {
					switch (meta.type) {
						case 'activate':
							ms = { ...INIT_STATE, active: true, from: meta.from, to: meta.to, query: meta.query, queryVersion:0, dataVersion: -1 };
							break;
						case 'update':
							ms = { ...prev, from: meta.from, to: meta.to, query: meta.query };
							if (prev.query !== meta.query) ms.queryVersion++;
							ms.commit = meta.commit ? "exact" : "no";
							break;
						case 'data':
							ms = { ...prev, mentions: meta.mentions, selectedIdx: meta.selectedIdx, dataVersion: meta.version };
							break;
						case 'deactivate':
							if (meta.init) ms = { ...INIT_STATE };
							else ms = { ...prev, active: false };
							break;
						case 'commit':
							ms = { ...prev, commit: "any" };
							break;
						case 'move':
							if (typeof meta.direction === 'number') {
								let selectedIdx = (prev.selectedIdx ?? -1) + meta.direction;
								if (selectedIdx < 0) selectedIdx = prev.mentions.length - 1;
								else if (selectedIdx >= prev.mentions.length) selectedIdx = 0;
								ms = { ...prev, selectedIdx };
							} else if (typeof meta.to === 'number') {
								let selectedIdx = meta.to;
								if (selectedIdx < 0) selectedIdx = 0;
								else if (selectedIdx >= prev.mentions.length) selectedIdx = prev.mentions.length - 1;
								ms = { ...prev, selectedIdx };
							}
							break;
					}
					//console.log("=>> apply transaction: "+cleanTrans(meta)+"\n\tstate: "+cleanState(ms));
				}
				return ms;
			}
		},

		view(view:EditorView):PluginView {
			let suggestionsPanel:HTMLDivElement | null = null;

			function generateMentionRow(view:EditorView, idx:number, mi: MentionSuggestion, selected = false) {
				const row = document.createElement("div");
				row.className = "us-item";
				if (selected) row.classList.add("us-item-selected");
				row.setAttribute("data-mention-idx", idx.toString());
				// Generate icon/avatar element
				if (mi.avatar) {
					const img = document.createElement("img");
					img.classList.add("us-avatar");
					img.classList.add("us-avatar-img");
					img.setAttribute("alt", mi.login);
					img.setAttribute("src", mi.avatar);
					row.appendChild(img);
				} else {
					const i:StyledIcon = Icon.parse(mi.icon || "user");
					const icn = document.createElement("span");
					if (i.clazz) icn.className = i.clazz;
					icn.classList.add("fi");
					icn.classList.add("us-avatar");
					icn.classList.add("us-avatar-icon");
					if (i.style) icn.setAttribute("style", i.style);
					icn.innerHTML = i.entity;
					row.appendChild(icn);
				}
				// Generate name block
				const block = document.createElement("div");
				block.className = "us-item-desc";
				row.appendChild(block);
				const name = document.createElement("div");
				name.className = "us-item-title";
				name.innerText = mi.name;
				block.appendChild(name)
				const info = document.createElement("div");
				info.className = "us-item-info";
				const loginInfo = document.createElement("span");
				loginInfo.className = "info-info";
				loginInfo.innerText = "@"+mi.login;
				info.appendChild(loginInfo);
				block.appendChild(info);

				row.addEventListener("mouseenter", (e:MouseEvent) => {
					//console.log("=> dispatch move TO");
					view.dispatch(view.state.tr.setMeta(KEY, <MentionTransaction>{ type: 'move', to: idx })/*.setMeta("addToHistory", false)*/);
				});
				row.addEventListener("click", (e:MouseEvent) => {
					view.dispatch(view.state.tr.setMeta(KEY, <MentionTransaction>{ type: 'commit' }));
				});
				return row;
			}

			function selectRow(idx:number) {
				if (!suggestionsPanel) return;
				suggestionsPanel.querySelectorAll(".us-item").forEach((el, i) => {
					if (i === idx) {
						el.classList.add("us-item-selected");
					} else {
						el.classList.remove("us-item-selected");
					}
				});
			}

			function currentMention(state:MentionState):MentionSuggestion|null {
				if (state.selectedIdx >= 0 && state.selectedIdx < state.mentions.length) {
					return state.mentions[state.selectedIdx];
				}
				return null;
			}

			function show() {
				suggestionsPanel = document.createElement("div");
				suggestionsPanel.className = "fd-drop fd-user-select-drop mention-suggestions";
				document.body.appendChild(suggestionsPanel);
				// Prevent click-through
				suggestionsPanel.addEventListener("mousedown", (e:MouseEvent) => {
					e.preventDefault();
				});
			}

			function hide() {
				suggestionsPanel.remove();
				suggestionsPanel = null;
			}

			function updateDropDownPosition(view:EditorView, ms:MentionState) {
				try {
					const start = view.coordsAtPos(ms.from - 1);
					suggestionsPanel.style.left = start.left+"px";
					suggestionsPanel.style.top = (start.bottom + 4)+"px";
				} catch (e) {
					console.error(e);
				}
			}

			/** Update the content of the suggestions panel */
			function updatePanelRows(view:EditorView, query:string, mentions:MentionSuggestion[], selected:number) {
				if (suggestionsPanel) {
					suggestionsPanel.innerHTML = "";	// empty previous content
					if (mentions?.length > 0) {
						let idx = 0;
						for (let mi of mentions) {
							const isSelected = idx === selected;
							suggestionsPanel.appendChild(generateMentionRow(view, idx++, mi, isSelected));
						}
					} else {
						const nf = document.createElement("div");
						nf.className = "us-not-found";
						nf.innerText = msg("suggest.not.found", query);
						suggestionsPanel.appendChild(nf);
					}
				}
			}

			async function query(view:EditorView, query:string, version:number):Promise<void> {
				let suggestions: MentionSuggestions;
				if (query === START_QUERY) {
					// Set data to empty array
					suggestions = { query: START_QUERY, items: [] };
				} else {
					// Set data to query result
					suggestions = await options.suggest(query);
				}

				// Generate mention objects
				let mentions:MentionSuggestion[];
				let selectedIdx;
				if (suggestions?.items?.length > 0) {
					mentions = suggestions.items;
					selectedIdx = 0;	// select the first
				} else {
					mentions = [];
					selectedIdx = -1;
				}
				const mt:MentionTransaction = { type: 'data', mentions, selectedIdx, version };
				const tr = view.state.tr.setMeta(KEY, mt);
				//console.log("=> dispatch data len="+mentions.length);
				view.dispatch(tr);
			}

			function commitMention(view:EditorView, mention:MentionSuggestion|null|undefined, from:number, to:number, matchQuery: string) {
				const tr = view.state.tr;
				if (mention && (!matchQuery || matchQuery === mention.login)) {
					const node = view.state.schema.nodes.mention.create({ id: "@"+mention.login, label: "@"+mention.login, title: mention.name, href: mention.url });
					tr.replaceWith(from, to, node).scrollIntoView();
					let pos = from + 1;
					if (!matchQuery) {
						tr.insert(pos, view.state.schema.text(" "));
						pos++;
						tr.setSelection(TextSelection.create(tr.doc, pos));
					}
				}
				tr.setMeta(KEY, { type: 'deactivate', init:true })/*.setMeta("addToHistory", false)*/;
				//console.log("=> dispatch commit+deactivate");
				view.dispatch(tr);
			}

			return {
				update(view:EditorView, prevState:EditorState) {
					const ms = KEY.getState(view.state);
					let old = KEY.getState(prevState);

					if (!ms || ms === old) return;	// not for me || no change
					if (!old) old = INIT_STATE;

					try {
						//dumpState("old: ", old);
						//dumpState(" ms: ", ms);

						if (!old.active && ms.active) show();
						else if (old.active && !ms.active) hide();

						// Ask backend for a data if needed
						if (ms.queryVersion !== ms.dataVersion) {
							query(view, ms.query, ms.queryVersion);
						} else if (ms.commit !== "no") {
							commitMention(view, currentMention(ms), ms.from-1, ms.to, ms.commit === "exact" ? ms.query : null);
						}

						if (suggestionsPanel) {
							updateDropDownPosition(view, ms);
							if (old.dataVersion !== ms.dataVersion) {
								// Dropdown needs to be updated (new data arrived)
								updatePanelRows(view, ms.query, ms.mentions, ms.selectedIdx ?? -1);
							} else if (old.selectedIdx !== ms.selectedIdx) {
								//console.log("\tselection changed from "+old.selectedIdx+" to "+ms.selectedIdx);
								selectRow(ms.selectedIdx ?? -1);
							}
						}
					} finally {
						old = ms;
					}
				},
				destroy() {
					if (suggestionsPanel != null) hide();
				}
			}
		}
	});
}